import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  ArrowPathIcon,
  ChartBarIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, SortableTableHeader, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import { modulesService } from '../../services/modules'
import { estoqueService } from '../../services/estoque'
import { marketingSolicitacaoService } from '../../services/marketingSolicitacao'
import type {
  MarketingRequest,
  MarketingRequestPriority,
  MarketingRequestStage,
  MarketingRequestStockAllocationItem,
  MarketingRequestStockIssueInput,
} from '../../types/marketingSolicitacao'
import SolicitacaoDashboard from './SolicitacaoDashboard'
import SolicitacaoDetailDrawer from './SolicitacaoDetailDrawer'
import SolicitacaoEstoqueBaixaModal from './SolicitacaoEstoqueBaixaModal'
import SolicitacaoFormModal, { type SolicitacaoFormSubmitPayload } from './SolicitacaoFormModal'
import {
  DEFAULT_STAGE_COLOR,
  MODULE_NAME,
  PAGE_SIZE,
  PERM_LOADING,
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  PRIORITY_OPTIONS,
  PRIORITY_SORT_RANK,
  SELECT_CLASS,
  buildStockAllocationItems,
  formatDateOnlyPtBr,
  formatDateTimePtBr,
  formatRequestNumber,
  formatRequesterWithDepartment,
  hexToRgba,
  isMarketingRequestPriority,
  isRequestClosed,
  isStockDeductibleItemType,
  summarizeItems,
  tabButtonClass,
} from './solicitacaoUi'

type ViewMode = 'lista' | 'kanban' | 'dashboard'
type SortKey = 'number' | 'title' | 'date' | 'needed_on' | 'requester' | 'priority' | 'stage'

type PendingFinalize =
  | {
      kind: 'edit'
      payload: SolicitacaoFormSubmitPayload
      previous: MarketingRequest
    }
  | {
      kind: 'kanban'
      request: MarketingRequest
      stageId: string
    }

function KanbanCard({
  request,
  onClick,
  disabled,
}: {
  request: MarketingRequest
  onClick: () => void
  disabled?: boolean
}) {
  const dragMovedRef = React.useRef(false)
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: request.id,
    data: { request },
    disabled: Boolean(disabled),
  })
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined

  return (
    <button
      type="button"
      ref={setNodeRef}
      style={style}
      {...(disabled ? {} : listeners)}
      {...attributes}
      onPointerDown={() => {
        dragMovedRef.current = false
      }}
      onPointerMove={() => {
        if (!disabled) dragMovedRef.current = true
      }}
      onClick={() => {
        if (dragMovedRef.current || isDragging) return
        onClick()
      }}
      className={`w-full rounded-lg border border-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-primary-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-500 ${
        isDragging ? 'opacity-60' : ''
      } ${disabled ? 'cursor-default' : ''}`}
    >
      <div className="flex min-w-0 items-baseline gap-2">
        <div className="shrink-0 text-sm font-semibold text-gray-900 dark:text-white">
          {formatRequestNumber(request.request_number)}
        </div>
        <div className="min-w-0 truncate text-left text-xs text-gray-500 dark:text-gray-400">
          {formatRequesterWithDepartment(request.requester)}
        </div>
      </div>
      <div className="mt-1 truncate text-sm font-medium text-gray-900 dark:text-white" title={request.title || undefined}>
        {request.title?.trim() || 'Sem título'}
      </div>
      <div className="mt-1.5">
        {(() => {
          const priority = isMarketingRequestPriority(request.priority) ? request.priority : 'medium'
          return (
            <span
              className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${PRIORITY_COLORS[priority]}`}
            >
              {PRIORITY_LABELS[priority]}
            </span>
          )
        })()}
      </div>
      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Responsável: {request.handler?.name || '—'}
      </div>
      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        Para quando: {formatDateOnlyPtBr(request.needed_on)}
      </div>
      <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
        {summarizeItems(request.items)}
      </div>
      <div className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
        {formatDateTimePtBr(request.created_at)}
      </div>
    </button>
  )
}

function KanbanColumn({
  stage,
  requests,
  onCardClick,
}: {
  stage: MarketingRequestStage
  requests: MarketingRequest[]
  onCardClick: (request: MarketingRequest) => void
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id })
  const color = stage.color || DEFAULT_STAGE_COLOR

  return (
    <div className="flex w-64 shrink-0 flex-col">
      <div
        className="rounded-t-lg border border-b-0 px-3 py-2"
        style={{
          borderColor: hexToRgba(color, 0.35),
          backgroundColor: hexToRgba(color, 0.08),
        }}
      >
        <div className="mb-2 h-1 rounded" style={{ backgroundColor: color }} />
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-semibold" style={{ color }}>
            {stage.name}
          </span>
          <span className="text-xs" style={{ color }}>
            {requests.length}
          </span>
        </div>
      </div>
      <div
        ref={setNodeRef}
        className={`min-h-[320px] space-y-2 overflow-y-auto rounded-b-lg border p-2 ${
          isOver ? 'border-primary-400 bg-primary-50/40 dark:bg-primary-900/10' : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/30'
        }`}
        style={{ maxHeight: '70vh' }}
      >
        {requests.map((request) => (
          <KanbanCard
            key={request.id}
            request={request}
            disabled={isRequestClosed(request) || Boolean(stage.is_closed)}
            onClick={() => onCardClick(request)}
          />
        ))}
      </div>
    </div>
  )
}

const SolicitacaoPage: React.FC = () => {
  const { user } = useAuthContext()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate, loading: updatePermLoading } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete, loading: deletePermLoading } = usePermission(MODULE_NAME, 'delete')
  const { hasPermission: canViewValues, loading: viewValuesLoading } = usePermission(
    MODULE_NAME,
    'view_values'
  )
  const { logView, logCreate, logUpdate, logDelete } = useUserLogs()

  const [moduleTitle, setModuleTitle] = useState('Solicitação')
  const [moduleDescription, setModuleDescription] = useState(
    'Solicitações de itens e serviços de marketing'
  )
  const [viewMode, setViewMode] = useState<ViewMode>('lista')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [requests, setRequests] = useState<MarketingRequest[]>([])
  const [stages, setStages] = useState<MarketingRequestStage[]>([])

  const [search, setSearch] = useState('')
  const [stageFilter, setStageFilter] = useState('')
  const [requesterFilter, setRequesterFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<MarketingRequest | null>(null)
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState<MarketingRequest | null>(null)
  const [baixaOpen, setBaixaOpen] = useState(false)
  const [baixaBusy, setBaixaBusy] = useState(false)
  const [baixaItems, setBaixaItems] = useState<MarketingRequestStockAllocationItem[]>([])
  const [baixaRequestNumber, setBaixaRequestNumber] = useState(0)
  const [pendingFinalize, setPendingFinalize] = useState<PendingFinalize | null>(null)

  const showKanban = !viewValuesLoading && canViewValues

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  useEffect(() => {
    modulesService.getByName(MODULE_NAME).then((result) => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [])

  const loadAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [stagesData, requestsData] = await Promise.all([
        marketingSolicitacaoService.listStages(),
        marketingSolicitacaoService.listRequests({
          stageIds: stageFilter ? [stageFilter] : undefined,
          dateFrom: dateFrom ? `${dateFrom}T00:00:00` : undefined,
          dateTo: dateTo ? `${dateTo}T23:59:59` : undefined,
        }),
      ])
      setStages(stagesData)
      setRequests(requestsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar solicitações.')
    } finally {
      setLoading(false)
    }
  }, [stageFilter, dateFrom, dateTo])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  useEffect(() => {
    void logView('marketing_request', null, 'Visualizou tela de solicitações de marketing')
  }, [logView])

  useEffect(() => {
    if (
      !viewValuesLoading &&
      !canViewValues &&
      (viewMode === 'kanban' || viewMode === 'dashboard')
    ) {
      setViewMode('lista')
    }
  }, [viewValuesLoading, canViewValues, viewMode])

  const requesterOptions = useMemo(() => {
    const byId = new Map<string, MarketingRequest['requester']>()
    for (const row of requests) {
      if (!row.requester_id || !row.requester) continue
      if (!byId.has(row.requester_id)) byId.set(row.requester_id, row.requester)
    }
    return Array.from(byId.entries())
      .map(([id, requester]) => ({
        id,
        label: formatRequesterWithDepartment(requester),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))
  }, [requests])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return requests.filter((row) => {
      if (requesterFilter && row.requester_id !== requesterFilter) return false
      if (priorityFilter) {
        const priority = isMarketingRequestPriority(row.priority) ? row.priority : 'medium'
        if (priority !== priorityFilter) return false
      }

      if (!term) return true

      const numberMatch =
        String(row.request_number).includes(term.replace(/^#/, '')) ||
        formatRequestNumber(row.request_number).toLowerCase().includes(term)
      const requesterLabel = formatRequesterWithDepartment(row.requester).toLowerCase()
      const requesterMatch = requesterLabel.includes(term)
      const handlerMatch = (row.handler?.name || '').toLowerCase().includes(term)
      const titleMatch = (row.title || '').toLowerCase().includes(term)
      const notesMatch = (row.notes || '').toLowerCase().includes(term)
      const stageMatch = (row.stage?.name || '').toLowerCase().includes(term)
      const itemsMatch = (row.items || []).some((item) => {
        const typeMatch = item.item_type.includes(term)
        const detailsMatch = Object.values(item.details || {}).some((value) =>
          String(value || '')
            .toLowerCase()
            .includes(term)
        )
        return typeMatch || detailsMatch
      })
      return numberMatch || titleMatch || requesterMatch || handlerMatch || notesMatch || stageMatch || itemsMatch
    })
  }, [requests, search, requesterFilter, priorityFilter])

  const sorted = useMemo(() => {
    const rows = [...filtered]
    rows.sort((a, b) => {
      let av: string | number = ''
      let bv: string | number = ''
      if (sortKey === 'number') {
        av = a.request_number
        bv = b.request_number
      } else if (sortKey === 'title') {
        av = (a.title || '').toLowerCase()
        bv = (b.title || '').toLowerCase()
      } else if (sortKey === 'date') {
        av = new Date(a.created_at).getTime()
        bv = new Date(b.created_at).getTime()
      } else if (sortKey === 'needed_on') {
        av = a.needed_on || ''
        bv = b.needed_on || ''
      } else if (sortKey === 'requester') {
        av = formatRequesterWithDepartment(a.requester).toLowerCase()
        bv = formatRequesterWithDepartment(b.requester).toLowerCase()
      } else if (sortKey === 'priority') {
        const pa = isMarketingRequestPriority(a.priority) ? a.priority : 'medium'
        const pb = isMarketingRequestPriority(b.priority) ? b.priority : 'medium'
        av = PRIORITY_SORT_RANK[pa]
        bv = PRIORITY_SORT_RANK[pb]
      } else if (sortKey === 'stage') {
        av = (a.stage?.name || '').toLowerCase()
        bv = (b.stage?.name || '').toLowerCase()
      }
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortOrder === 'asc' ? av - bv : bv - av
      }
      if (av < bv) return sortOrder === 'asc' ? -1 : 1
      if (av > bv) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
    return rows
  }, [filtered, sortKey, sortOrder])

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE) || 1)
  const startIndex = (page - 1) * PAGE_SIZE
  const endIndex = Math.min(page * PAGE_SIZE, sorted.length)
  const pageRows = useMemo(
    () => sorted.slice(startIndex, startIndex + PAGE_SIZE),
    [sorted, startIndex]
  )

  const handlePageChange = (nextPage: number) => setPage(nextPage)
  const handlePreviousPage = () => {
    if (page > 1) setPage((p) => p - 1)
  }
  const handleNextPage = () => {
    if (page < totalPages) setPage((p) => p + 1)
  }

  const byStage = useMemo(() => {
    const map: Record<string, MarketingRequest[]> = {}
    for (const stage of stages) map[stage.id] = []
    for (const request of filtered) {
      if (!map[request.stage_id]) map[request.stage_id] = []
      map[request.stage_id].push(request)
    }
    return map
  }, [stages, filtered])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortOrder(key === 'date' || key === 'number' || key === 'needed_on' ? 'desc' : 'asc')
    }
    setPage(1)
  }

  const clearFilters = () => {
    setSearch('')
    setStageFilter('')
    setRequesterFilter('')
    setPriorityFilter('')
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditing(null)
  }

  const closeBaixa = () => {
    if (baixaBusy) return
    const wasEditFinalize = pendingFinalize?.kind === 'edit'
    setBaixaOpen(false)
    setBaixaItems([])
    setPendingFinalize(null)
    if (wasEditFinalize) {
      setFormOpen(true)
    }
  }

  const openNew = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (request: MarketingRequest) => {
    if (updatePermLoading || !canUpdate || request.deleted_at) return
    if (isRequestClosed(request, stages)) {
      setError('Solicitação finalizada: não é possível alterar.')
      return
    }
    setEditing(request)
    setFormOpen(true)
    setSelected(null)
  }

  const movementIdOf = (mov: unknown): string | null => {
    if (!mov) return null
    if (Array.isArray(mov)) {
      const first = mov[0] as { id?: string } | undefined
      return first?.id ?? null
    }
    return (mov as { id?: string }).id ?? null
  }

  const applyStockOuts = async (
    requestNumber: number,
    allocations: MarketingRequestStockAllocationItem[]
  ): Promise<MarketingRequestStockIssueInput[]> => {
    const issues: MarketingRequestStockIssueInput[] = []
    const description = `Solicitação ${formatRequestNumber(requestNumber)} — baixa na finalização`

    for (const item of allocations) {
      for (const line of item.lines) {
        const movement = await estoqueService.registerMovimentacao({
          item_id: item.stock_item_id,
          location_id: line.location_id,
          movement_type: 'out',
          quantity: line.quantity,
          description,
        })
        issues.push({
          stock_item_id: item.stock_item_id,
          location_id: line.location_id,
          quantity: line.quantity,
          movement_id: movementIdOf(movement),
        })
        void logCreate(
          'marketing_stock_movement',
          movementIdOf(movement),
          description,
          {
            request_number: requestNumber,
            item_id: item.stock_item_id,
            location_id: line.location_id,
            quantity: line.quantity,
            movement_type: 'out',
          }
        )
      }
    }
    return issues
  }

  const persistEdit = async (
    payload: SolicitacaoFormSubmitPayload,
    previous: MarketingRequest,
    options?: { closed_at?: string | null }
  ) => {
    if (!user?.id) throw new Error('Usuário não autenticado.')

    const priority: MarketingRequestPriority = isMarketingRequestPriority(payload.priority)
      ? payload.priority
      : isMarketingRequestPriority(previous.priority)
        ? previous.priority
        : 'medium'

    const updated = await marketingSolicitacaoService.updateRequest(
      previous.id,
      {
        stage_id: payload.stageId || previous.stage_id,
        handler_id: payload.handlerId ?? null,
        title: payload.title,
        notes: payload.notes,
        needed_on: payload.neededOn,
        priority,
        closed_at: options?.closed_at,
        items: payload.items,
      },
      { deleted_by: user.id }
    )

    for (const attachmentId of payload.attachmentIdsToDelete) {
      await marketingSolicitacaoService.softDeleteAttachment(attachmentId, {
        deleted_by: user.id,
      })
    }

    for (const file of payload.files) {
      const attachment = await marketingSolicitacaoService.uploadAttachment(
        updated.id,
        file,
        user.id
      )
      void logCreate(
        'marketing_request_attachment',
        attachment.id,
        `Anexou arquivo à solicitação ${formatRequestNumber(updated.request_number)}: ${attachment.file_name}`,
        {
          request_id: updated.id,
          request_number: updated.request_number,
          file_name: attachment.file_name,
          file_size: attachment.file_size,
          mime_type: attachment.mime_type,
        }
      )
    }

    const refreshed =
      payload.files.length || payload.attachmentIdsToDelete.length
        ? (await marketingSolicitacaoService.getRequest(updated.id)) || updated
        : updated

    void logUpdate(
      'marketing_request',
      refreshed.id,
      options?.closed_at
        ? `Finalizou solicitação de marketing ${formatRequestNumber(refreshed.request_number)}`
        : `Atualizou solicitação de marketing ${formatRequestNumber(refreshed.request_number)}`,
      {
        stage_id: previous.stage_id,
        stage_name: previous.stage?.name,
        handler_id: previous.handler_id,
        handler_name: previous.handler?.name,
        title: previous.title,
        notes: previous.notes,
        needed_on: previous.needed_on,
        priority: previous.priority,
        closed_at: previous.closed_at,
        items_count: previous.items?.length || 0,
      },
      {
        stage_id: refreshed.stage_id,
        stage_name: refreshed.stage?.name,
        handler_id: refreshed.handler_id,
        handler_name: refreshed.handler?.name,
        title: refreshed.title,
        notes: refreshed.notes,
        needed_on: refreshed.needed_on,
        priority: refreshed.priority,
        closed_at: refreshed.closed_at,
        items_count: refreshed.items?.length || 0,
        attachments_added: payload.files.length,
        attachments_removed: payload.attachmentIdsToDelete.length,
      }
    )

    return refreshed
  }

  const handleSubmitForm = async (payload: SolicitacaoFormSubmitPayload): Promise<boolean | void> => {
    if (!user?.id) {
      setError('Usuário não autenticado.')
      return false
    }

    const isEdit = Boolean(editing?.id)
    if (isEdit && (updatePermLoading || !canUpdate)) {
      setError('Você não tem permissão para editar solicitações.')
      return false
    }
    if (isEdit && editing && isRequestClosed(editing, stages)) {
      setError('Solicitação finalizada: não é possível alterar.')
      return false
    }

    setSaving(true)
    setError(null)
    try {
      if (isEdit && editing) {
        const targetStageId = payload.stageId || editing.stage_id
        const targetStage = stages.find((s) => s.id === targetStageId)
        const needsFinalize = Boolean(targetStage?.is_closed) && !editing.closed_at

        if (needsFinalize) {
          const confirmed = window.confirm(
            'Esta etapa encerra a solicitação. Deseja finalizar?'
          )
          if (!confirmed) return false

          const hasStockType = payload.items.some((item) =>
            isStockDeductibleItemType(item.item_type)
          )
          if (hasStockType) {
            const allocationItems = buildStockAllocationItems(payload.items)
            if (allocationItems.length > 0) {
              setPendingFinalize({ kind: 'edit', payload, previous: editing })
              setBaixaItems(allocationItems)
              setBaixaRequestNumber(editing.request_number)
              setBaixaOpen(true)
              return false
            }
          }

          const refreshed = await persistEdit(payload, editing, {
            closed_at: new Date().toISOString(),
          })
          closeForm()
          await loadAll()
          setSelected(refreshed)
          return
        }

        const refreshed = await persistEdit(payload, editing)
        closeForm()
        await loadAll()
        setSelected(refreshed)
        return
      }

      const created = await marketingSolicitacaoService.createRequest(
        { title: payload.title, notes: payload.notes, needed_on: payload.neededOn, items: payload.items },
        user.id
      )

      for (const file of payload.files) {
        const attachment = await marketingSolicitacaoService.uploadAttachment(
          created.id,
          file,
          user.id
        )
        void logCreate(
          'marketing_request_attachment',
          attachment.id,
          `Anexou arquivo à solicitação ${formatRequestNumber(created.request_number)}: ${attachment.file_name}`,
          {
            request_id: created.id,
            request_number: created.request_number,
            file_name: attachment.file_name,
            file_size: attachment.file_size,
            mime_type: attachment.mime_type,
          }
        )
      }

      const refreshed = payload.files.length
        ? (await marketingSolicitacaoService.getRequest(created.id)) || created
        : created

      void logCreate(
        'marketing_request',
        refreshed.id,
        `Criou solicitação de marketing ${formatRequestNumber(refreshed.request_number)}`,
        {
          request_number: refreshed.request_number,
          title: refreshed.title,
          items_count: refreshed.items?.length || 0,
          item_types: (refreshed.items || []).map((item) => item.item_type),
          attachments_count: payload.files.length,
          stage_id: refreshed.stage_id,
          stage_name: refreshed.stage?.name,
          handler_id: refreshed.handler_id,
          handler_name: refreshed.handler?.name,
          needed_on: refreshed.needed_on,
        }
      )
      closeForm()
      await loadAll()
      setSelected(refreshed)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar solicitação.')
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleConfirmBaixa = async (allocations: MarketingRequestStockAllocationItem[]) => {
    if (!pendingFinalize || !user?.id) return
    setBaixaBusy(true)
    setError(null)
    try {
      const closedAt = new Date().toISOString()

      if (pendingFinalize.kind === 'edit') {
        const refreshed = await persistEdit(pendingFinalize.payload, pendingFinalize.previous, {
          closed_at: closedAt,
        })
        const issues = await applyStockOuts(refreshed.request_number, allocations)
        await marketingSolicitacaoService.createStockIssues(refreshed.id, issues, user.id)
        setBaixaOpen(false)
        setPendingFinalize(null)
        setBaixaItems([])
        closeForm()
        await loadAll()
        setSelected(refreshed)
        return
      }

      const previous = pendingFinalize.request
      const previousStage = stages.find((s) => s.id === previous.stage_id)
      const updated = await marketingSolicitacaoService.updateRequestStage(
        previous.id,
        pendingFinalize.stageId,
        { closed_at: closedAt }
      )
      const issues = await applyStockOuts(updated.request_number, allocations)
      await marketingSolicitacaoService.createStockIssues(updated.id, issues, user.id)
      const nextStage = stages.find((s) => s.id === updated.stage_id)
      void logUpdate(
        'marketing_request',
        updated.id,
        `Finalizou solicitação de marketing ${formatRequestNumber(updated.request_number)}`,
        {
          stage_id: previous.stage_id,
          stage_name: previousStage?.name || previous.stage?.name,
          closed_at: previous.closed_at,
        },
        {
          stage_id: updated.stage_id,
          stage_name: nextStage?.name || updated.stage?.name,
          closed_at: updated.closed_at,
        }
      )
      setBaixaOpen(false)
      setPendingFinalize(null)
      setBaixaItems([])
      await loadAll()
      setSelected(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao finalizar e baixar estoque.')
    } finally {
      setBaixaBusy(false)
    }
  }

  const openDetail = (request: MarketingRequest) => {
    setSelected(request)
    void logView(
      'marketing_request',
      request.id,
      `Visualizou solicitação de marketing ${formatRequestNumber(request.request_number)}`
    )
  }

  const handleDelete = async (requestId: string) => {
    if (deletePermLoading || !canDelete) {
      setError('Você não tem permissão para excluir solicitações.')
      return
    }
    const target = requests.find((r) => r.id === requestId) || selected
    if (!target || target.deleted_at) return
    if (isRequestClosed(target, stages)) {
      setError('Solicitação finalizada: não é possível excluir.')
      return
    }

    if (
      !window.confirm(
        'Excluir esta solicitação? A exclusão é lógica: some da lista, ' +
          'mas o registro permanece com usuário e data da exclusão.'
      )
    ) {
      return
    }

    try {
      await marketingSolicitacaoService.softDeleteRequest(requestId, {
        deleted_by: user?.id ?? null,
      })
      void logDelete(
        'marketing_request',
        requestId,
        `Excluiu solicitação de marketing ${formatRequestNumber(target.request_number)}`,
        {
          request_number: target.request_number,
          stage_id: target.stage_id,
          stage_name: target.stage?.name,
        }
      )
      if (selected?.id === requestId) setSelected(null)
      await loadAll()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível excluir esta solicitação.')
    }
  }

  const handleChangeStage = async (
    requestId: string,
    stageId: string,
    options?: { closed_at?: string | null; finalized?: boolean }
  ) => {
    if (updatePermLoading || !canUpdate) {
      throw new Error('Você não tem permissão para editar solicitações.')
    }
    const previous = requests.find((r) => r.id === requestId)
    if (previous && isRequestClosed(previous, stages) && !options?.finalized) {
      throw new Error('Solicitação finalizada: não é possível alterar a etapa.')
    }
    const previousStage = stages.find((s) => s.id === previous?.stage_id)
    const updated = await marketingSolicitacaoService.updateRequestStage(requestId, stageId, {
      closed_at: options?.closed_at,
    })
    const nextStage = stages.find((s) => s.id === updated.stage_id)
    void logUpdate(
      'marketing_request',
      updated.id,
      options?.finalized
        ? `Finalizou solicitação de marketing ${formatRequestNumber(updated.request_number)}`
        : `Alterou etapa da solicitação ${formatRequestNumber(updated.request_number)}`,
      {
        stage_id: previous?.stage_id,
        stage_name: previousStage?.name || previous?.stage?.name,
        closed_at: previous?.closed_at,
      },
      {
        stage_id: updated.stage_id,
        stage_name: nextStage?.name || updated.stage?.name,
        closed_at: updated.closed_at,
      }
    )
    setRequests((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
    setSelected((current) => (current?.id === updated.id ? updated : current))
  }

  const onDragEnd = async (event: DragEndEvent) => {
    if (updatePermLoading || !canUpdate) return
    const requestId = String(event.active.id)
    const overId = event.over?.id ? String(event.over.id) : null
    if (!overId) return

    const request = requests.find((r) => r.id === requestId)
    if (!request || request.deleted_at) return
    if (isRequestClosed(request, stages)) {
      setError('Solicitação finalizada: não é possível alterar a etapa.')
      return
    }

    let targetStageId = overId
    if (!stages.some((s) => s.id === overId)) {
      const overRequest = requests.find((r) => r.id === overId)
      if (!overRequest) return
      targetStageId = overRequest.stage_id
    }

    if (request.stage_id === targetStageId) return

    const targetStage = stages.find((s) => s.id === targetStageId)
    try {
      if (targetStage?.is_closed && !request.closed_at) {
        const confirmed = window.confirm(
          'Esta etapa encerra a solicitação. Deseja finalizar?'
        )
        if (!confirmed) return

        const hasStockType = (request.items || []).some((item) =>
          isStockDeductibleItemType(item.item_type)
        )
        if (hasStockType) {
          const allocationItems = buildStockAllocationItems(request.items || [])
          if (allocationItems.length > 0) {
            setPendingFinalize({ kind: 'kanban', request, stageId: targetStageId })
            setBaixaItems(allocationItems)
            setBaixaRequestNumber(request.request_number)
            setBaixaOpen(true)
            return
          }
        }

        await handleChangeStage(requestId, targetStageId, {
          closed_at: new Date().toISOString(),
          finalized: true,
        })
        return
      }

      await handleChangeStage(requestId, targetStageId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao mover solicitação.')
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
      <PermissionGuard
        moduleName={MODULE_NAME}
        permissionType="show"
        loadingComponent={PERM_LOADING}
        fallback={
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <Text size="xl" className="mb-2 text-gray-500 dark:text-gray-400">
                Acesso Negado
              </Text>
              <Text size="sm" color="muted">
                Você não tem permissão para acessar esta página.
              </Text>
            </div>
          </div>
        }
      >
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleTitle}
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                {moduleDescription}
              </Text>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void loadAll()}
                disabled={loading}
                className="inline-flex items-center gap-2"
              >
                <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
              {canInsert && (
                <PermissionGuard
                  moduleName={MODULE_NAME}
                  permissionType="insert"
                  loadingComponent={PERM_LOADING}
                  fallback={null}
                >
                  <Button variant="solid" onClick={openNew}>
                    Nova solicitação
                  </Button>
                </PermissionGuard>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mb-6 overflow-visible rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Buscar
                </label>
                <Input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                    setPage(1)
                  }}
                  placeholder="Número, solicitante, item..."
                  className="min-w-[200px]"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Etapa
                </label>
                <select
                  value={stageFilter}
                  onChange={(e) => {
                    setStageFilter(e.target.value)
                    setPage(1)
                  }}
                  className={SELECT_CLASS}
                >
                  <option value="">Todas</option>
                  {stages.map((stage) => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Solicitante
                </label>
                <select
                  value={requesterFilter}
                  onChange={(e) => {
                    setRequesterFilter(e.target.value)
                    setPage(1)
                  }}
                  className={`${SELECT_CLASS} min-w-[220px]`}
                >
                  <option value="">Todos</option>
                  {requesterOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Prioridade
                </label>
                <select
                  value={priorityFilter}
                  onChange={(e) => {
                    setPriorityFilter(e.target.value)
                    setPage(1)
                  }}
                  className={SELECT_CLASS}
                >
                  <option value="">Todas</option>
                  {PRIORITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  De
                </label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value)
                    setPage(1)
                  }}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  Até
                </label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value)
                    setPage(1)
                  }}
                />
              </div>
              <Button variant="outline" onClick={clearFilters}>
                Limpar filtros
              </Button>
            </div>
          </div>

          <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex gap-6">
              <button
                type="button"
                className={`${tabButtonClass(viewMode === 'lista')} inline-flex items-center gap-1.5`}
                onClick={() => setViewMode('lista')}
              >
                <TableCellsIcon className="h-4 w-4" />
                Lista
              </button>
              {showKanban && (
                <>
                  <button
                    type="button"
                    className={`${tabButtonClass(viewMode === 'kanban')} inline-flex items-center gap-1.5`}
                    onClick={() => setViewMode('kanban')}
                  >
                    <Squares2X2Icon className="h-4 w-4" />
                    Kanban
                  </button>
                  <button
                    type="button"
                    className={`${tabButtonClass(viewMode === 'dashboard')} inline-flex items-center gap-1.5`}
                    onClick={() => setViewMode('dashboard')}
                  >
                    <ChartBarIcon className="h-4 w-4" />
                    Dashboard
                  </button>
                </>
              )}
            </nav>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : showKanban && viewMode === 'dashboard' ? (
            <SolicitacaoDashboard requests={filtered} stages={stages} />
          ) : !showKanban || viewMode === 'lista' ? (
            <>
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/30">
                      <tr>
                        <SortableTableHeader
                          sortKey="number"
                          currentSort={sortKey}
                          sortOrder={sortOrder}
                          onSort={(key) => toggleSort(key as SortKey)}
                          className="px-4 py-3"
                        >
                          ID
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="title"
                          currentSort={sortKey}
                          sortOrder={sortOrder}
                          onSort={(key) => toggleSort(key as SortKey)}
                          className="px-4 py-3"
                        >
                          Título
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="date"
                          currentSort={sortKey}
                          sortOrder={sortOrder}
                          onSort={(key) => toggleSort(key as SortKey)}
                          className="px-4 py-3"
                        >
                          Data
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="needed_on"
                          currentSort={sortKey}
                          sortOrder={sortOrder}
                          onSort={(key) => toggleSort(key as SortKey)}
                          className="px-4 py-3"
                        >
                          Para quando
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="requester"
                          currentSort={sortKey}
                          sortOrder={sortOrder}
                          onSort={(key) => toggleSort(key as SortKey)}
                          className="px-4 py-3"
                        >
                          Solicitante
                        </SortableTableHeader>
                        <SortableTableHeader
                          sortKey="priority"
                          currentSort={sortKey}
                          sortOrder={sortOrder}
                          onSort={(key) => toggleSort(key as SortKey)}
                          className="px-4 py-3"
                        >
                          Prioridade
                        </SortableTableHeader>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                          Responsável
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                          Itens
                        </th>
                        <SortableTableHeader
                          sortKey="stage"
                          currentSort={sortKey}
                          sortOrder={sortOrder}
                          onSort={(key) => toggleSort(key as SortKey)}
                          className="px-4 py-3"
                        >
                          Etapa
                        </SortableTableHeader>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {pageRows.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                            Nenhuma solicitação encontrada.
                          </td>
                        </tr>
                      ) : (
                        pageRows.map((row) => (
                          <tr
                            key={row.id}
                            className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/20"
                            onClick={() => openDetail(row)}
                          >
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              {formatRequestNumber(row.request_number)}
                            </td>
                            <td className="max-w-xs px-4 py-3 text-sm text-gray-900 dark:text-white">
                              <span className="block truncate" title={row.title || undefined}>
                                {row.title?.trim() || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                              {formatDateTimePtBr(row.created_at)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                              {formatDateOnlyPtBr(row.needed_on)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                              {formatRequesterWithDepartment(row.requester)}
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                              {(() => {
                                const priority = isMarketingRequestPriority(row.priority)
                                  ? row.priority
                                  : 'medium'
                                return (
                                  <span
                                    className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${PRIORITY_COLORS[priority]}`}
                                  >
                                    {PRIORITY_LABELS[priority]}
                                  </span>
                                )
                              })()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                              {row.handler?.name || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                              {summarizeItems(row.items)}
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                              {(() => {
                                const stage =
                                  row.stage || stages.find((s) => s.id === row.stage_id) || null
                                if (!stage) return '—'
                                const color = stage.color || DEFAULT_STAGE_COLOR
                                return (
                                  <span
                                    className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                                    style={{
                                      backgroundColor: hexToRgba(color, 0.15),
                                      color,
                                      border: `1px solid ${hexToRgba(color, 0.4)}`,
                                    }}
                                  >
                                    {stage.name}
                                  </span>
                                )
                              })()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {!loading && (
                  <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300">
                        {sorted.length === 0
                          ? 'Nenhum registro'
                          : `Mostrando ${startIndex + 1} a ${endIndex} de ${sorted.length} registro${sorted.length !== 1 ? 's' : ''}`}
                      </Text>

                      {totalPages > 1 && (
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={handlePreviousPage}
                            disabled={page === 1}
                            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Anterior
                          </button>

                          <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNumber: number
                              if (totalPages <= 5) {
                                pageNumber = i + 1
                              } else if (page <= 3) {
                                pageNumber = i + 1
                              } else if (page >= totalPages - 2) {
                                pageNumber = totalPages - 4 + i
                              } else {
                                pageNumber = page - 2 + i
                              }
                              return (
                                <button
                                  key={pageNumber}
                                  type="button"
                                  onClick={() => handlePageChange(pageNumber)}
                                  className={`rounded-md px-3 py-2 text-sm font-medium ${
                                    page === pageNumber
                                      ? 'bg-primary-600 text-white'
                                      : 'border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                                  }`}
                                >
                                  {pageNumber}
                                </button>
                              )
                            })}
                          </div>

                          <button
                            type="button"
                            onClick={handleNextPage}
                            disabled={page === totalPages}
                            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Próximo
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : stages.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 px-4 py-16 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
              Cadastre etapas para usar o Kanban.
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => void onDragEnd(e)}>
              <div className="flex gap-3 overflow-x-auto pb-4">
                {stages.map((stage) => (
                  <KanbanColumn
                    key={stage.id}
                    stage={stage}
                    requests={byStage[stage.id] || []}
                    onCardClick={openDetail}
                  />
                ))}
              </div>
            </DndContext>
          )}
        </div>

        <SolicitacaoFormModal
          open={formOpen}
          busy={saving || baixaOpen}
          editing={editing}
          stages={stages}
          onClose={() => {
            if (baixaOpen) return
            closeForm()
          }}
          onSubmit={handleSubmitForm}
        />

        <SolicitacaoEstoqueBaixaModal
          open={baixaOpen}
          busy={baixaBusy}
          requestNumber={baixaRequestNumber}
          items={baixaItems}
          onClose={closeBaixa}
          onConfirm={handleConfirmBaixa}
        />

        <SolicitacaoDetailDrawer
          request={selected}
          stages={stages}
          onClose={() => setSelected(null)}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      </PermissionGuard>
    </div>
  )
}

export default SolicitacaoPage
